import { useMemo, useState } from 'react';
import styles from './LineChart.module.css';

export interface Series {
  key: string;
  label: string;
  color: string;
  /** One value per x position; null renders a gap rather than a false zero. */
  points: Array<number | null>;
  /** How the value reads in the tooltip. */
  format?: (v: number) => string;
}

export interface LineChartProps {
  labels: string[];
  series: Series[];
  height?: number;
  /** Fix the y-axis (percent metrics read 0–100 regardless of the data). */
  yMax?: number;
  yTicks?: number;
}

const PAD = { top: 16, right: 16, bottom: 28, left: 44 };

/**
 * Multi-series line chart with a shared crosshair tooltip.
 *
 * Hand-rolled SVG rather than a charting dependency: the page already draws
 * ~63 sparklines, and adding a chart runtime for one more surface is weight
 * the dashboard does not need. Reproduces the mark in Figma 3727:30363.
 */
export function LineChart({ labels, series, height = 380, yMax, yTicks = 5 }: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const width = 1000; // viewBox units; the SVG scales to its container

  const { min, max } = useMemo(() => {
    const vals = series.flatMap((s) => s.points.filter((v): v is number => v != null));
    if (vals.length === 0) return { min: 0, max: yMax ?? 100 };
    const hi = yMax ?? Math.max(...vals);
    const lo = Math.min(0, ...vals);
    return { min: lo, max: hi === lo ? lo + 1 : hi };
  }, [series, yMax]);

  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const x = (i: number): number =>
    labels.length <= 1 ? PAD.left + plotW / 2 : PAD.left + (i * plotW) / (labels.length - 1);
  const y = (v: number): number => PAD.top + plotH - ((v - min) / (max - min)) * plotH;

  const ticks = useMemo(
    () => Array.from({ length: yTicks + 1 }, (_, i) => min + ((max - min) * i) / yTicks),
    [min, max, yTicks],
  );

  // Show every label when there is room, otherwise thin them evenly.
  const labelStep = Math.max(1, Math.ceil(labels.length / 14));

  if (labels.length === 0 || series.length === 0) {
    return <p className={styles.empty}>Select at least one metric to plot.</p>;
  }

  return (
    <div className={styles.wrap}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Trend chart"
        onMouseLeave={() => { setHover(null); }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * width;
          const i = Math.round(((px - PAD.left) / plotW) * (labels.length - 1));
          setHover(Math.max(0, Math.min(labels.length - 1, i)));
        }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className={styles.grid} x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} />
            <text className={styles.axis} x={PAD.left - 8} y={y(t) + 3} textAnchor="end">
              {Math.round(t)}{yMax === 100 ? '%' : ''}
            </text>
          </g>
        ))}

        {labels.map((l, i) =>
          i % labelStep === 0 ? (
            <text key={l} className={styles.axis} x={x(i)} y={height - 8} textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}

        {hover != null && (
          <line className={styles.cursor} x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} />
        )}

        {series.map((s) => {
          const d = s.points
            .map((v, i) => (v == null ? null : `${i === 0 || s.points[i - 1] == null ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}`))
            .filter(Boolean)
            .join(' ');
          return (
            <g key={s.key}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {s.points.map((v, i) =>
                v == null ? null : (
                  <circle
                    key={`${s.key}-${String(i)}`}
                    className={styles.dot}
                    cx={x(i)}
                    cy={y(v)}
                    r={hover === i ? 4 : 3}
                    fill={s.color}
                  />
                ),
              )}
            </g>
          );
        })}
      </svg>

      {hover != null && (
        <div
          className={styles.tip}
          style={{
            left: `calc(${((x(hover) / width) * 100).toFixed(2)}% + ${hover > labels.length / 2 ? '-186px' : '14px'})`,
            top: 24,
          }}
        >
          <div className={styles.tipDate}>{labels[hover]}</div>
          {series.map((s) => {
            const v = s.points[hover];
            return (
              <div className={styles.tipRow} key={s.key}>
                <span className={styles.swatch} style={{ background: s.color }} />
                <span className={styles.tipName}>{s.label}</span>
                <span className={styles.tipVal}>
                  {v == null ? '—' : s.format ? s.format(v) : v.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
